'use client';

import { ComponentProps, ElementRef, forwardRef, LabelHTMLAttributes } from 'react';
import { Root, Item, Indicator } from '@radix-ui/react-radio-group';
import clsx from 'clsx';

export const RadioGroup = Root;

// RadioGroupItem is a component that renders a radio button in the radio group.
export const RadioGroupItem = forwardRef<ElementRef<typeof Item>, ComponentProps<typeof Item>>(
  function RadioGroupItem({ className, ...props }, ref) {
    return (
      <Item
        ref={ref}
        className={clsx(
          'relative  h-[1.5rem] w-[1.5rem]  rounded-special-circle border-thin border-t-neutral-tertiary bg-neutral-auxiliary-tinge data-[state=checked]:border-[2px] data-[state=checked]:border-brand-highlight data-[state=unchecked]:border-neutral-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      >
        <Indicator className="flex h-[1rem] w-[1rem] relative rounded-special-circle bg-brand-highlight left-[0.125rem] top-0" />
      </Item>
    );
  }
);
RadioGroupItem.displayName = Item.displayName;

// RadioGroupLabel is a component that renders a label for a radio button.
export function RadioGroupLabel({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={clsx('text-small font-medium text-neutral-primary cursor-pointer', className)}
      {...props}
    />
  );
}

// RadioButton is a component that combines a radio item and label.
export function RadioButton({
  id,
  value,
  label,
  disabled,
  className,
}: {
  id: string;
  value: string;
  label: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={clsx('flex items-center space-x-2', className)}>
      <RadioGroupItem id={id} value={value} disabled={disabled} />
      <RadioGroupLabel htmlFor={id}>{label}</RadioGroupLabel>
    </div>
  );
}
